import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { fetchEventSource } from '@microsoft/fetch-event-source';
import axios, { AxiosError } from 'axios';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { HttpHeaders } from '~/constants';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { FatalStreamError } from './agenticStreamError';
import { selectors } from './state-selectors';
export var APIHost = /*#__PURE__*/ function(APIHost) {
    APIHost["AI"] = "ai";
    APIHost["KYB"] = "kyb";
    APIHost["Plaid"] = "plaid";
    APIHost["Scout"] = "scout";
    APIHost["Mgmt"] = "mgmt";
    APIHost["Cato"] = "cato";
    APIHost["External"] = "external";
    return APIHost;
}({});
export const RETRY_BACKOFF_MS = 200;
// WHY jitter: a correlated upstream failure would otherwise send every browser's replays back in lockstep.
export const jitteredRetryBackoffMs = (random)=>RETRY_BACKOFF_MS * (0.5 + random);
const URL_BUSINESS_ID_REGEX = /\/businesses?\/(business-[a-zA-Z0-9_-]+)(?:\/|$|\?)/;
function isUrlBusinessIdStale(url, currentClaimsBusinessId) {
    if (currentClaimsBusinessId == null) return false;
    const match = url.match(URL_BUSINESS_ID_REGEX);
    const urlBusinessId = match === null || match === void 0 ? void 0 : match[1];
    if (urlBusinessId == null) return false;
    return urlBusinessId !== currentClaimsBusinessId;
}
function buildRequestInputs(options, state, getCookie) {
    const { apiHost, url, disableAuth, forceAuthorizationHeader, authToken } = options;
    const { apiGatewayHost, useAuthorizationHeader } = state.session;
    const fullUrl = apiGatewayHost + url;
    const prevCookie = getCookie();
    const headers = {};
    headers[HttpHeaders.ContentType] = 'application/json';
    const apiHostHeaderHosts = [
        "plaid",
        "scout",
        "kyb",
        "cato",
        "ai",
        "mgmt"
    ];
    if (apiHostHeaderHosts.includes(apiHost)) {
        headers['x-api-host'] = apiHost;
    }
    const actualAuthToken = (disableAuth || !useAuthorizationHeader) && !forceAuthorizationHeader ? null : authToken !== null && authToken !== void 0 ? authToken : state.authToken;
    if (actualAuthToken) {
        headers.Authorization = "Bearer ".concat(actualAuthToken);
    }
    if (window._sessionId) {
        headers[HttpHeaders.SessionId] = window._sessionId;
    }
    const requestId = generateUniqueId('request');
    headers[HttpHeaders.RequestId] = requestId;
    return {
        fullUrl,
        headers,
        requestId,
        prevCookie
    };
}
export function initSendRequest(param) {
    let { getState, getCookie, onCookieChange, onLogout, onNoAccess, onReportToSentry, onAuthTokenInResponse, onAuthTokenRevoked, onBiometricStaleToken = ()=>{}, delay = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms)), random = ()=>Math.random() } = param;
    const sendRequest = function sendRequest(options) {
        var _stateAtSend_claims, _stateAtSend_userInfo, _config_headers;
        const { method, params = {}, data = {}, apiHost, disableAuth, forceAuthorizationHeader, authToken, url, disableSentry, urlForSentry, config, disable403Handling, disableRetry } = options;
        const stateAtSend = getState();
        const identityAtSend = {
            claimsBusinessId: ((_stateAtSend_claims = stateAtSend.claims) === null || _stateAtSend_claims === void 0 ? void 0 : _stateAtSend_claims.businessId) || null,
            userInfoBusinessId: ((_stateAtSend_userInfo = stateAtSend.userInfo) === null || _stateAtSend_userInfo === void 0 ? void 0 : _stateAtSend_userInfo.businessId()) || null,
            isSwitchingBusiness: !!stateAtSend.isSwitchingBusiness
        };
        const { fullUrl, headers, requestId, prevCookie } = buildRequestInputs({
            apiHost,
            url,
            disableAuth,
            forceAuthorizationHeader,
            authToken
        }, stateAtSend, getCookie);
        const mergedHeaders = _object_spread({}, config === null || config === void 0 ? void 0 : config.headers, headers);
        if (config === null || config === void 0 ? void 0 : (_config_headers = config.headers) === null || _config_headers === void 0 ? void 0 : _config_headers[HttpHeaders.ContentType]) {
            mergedHeaders[HttpHeaders.ContentType] = config.headers[HttpHeaders.ContentType];
        }
        const axiosConfig = _object_spread_props(_object_spread({}, config), {
            withCredentials: true,
            params,
            headers: mergedHeaders
        });
        const attempt = ()=>axios.request(_object_spread_props(_object_spread({
                method,
                url: fullUrl
            }, axiosConfig), {
                data
            }));
        // WHY only GETs: Envoy kills an idle keepalive connection mid-request, but replaying a write can double-charge.
        const isReplayableGatewayFailure = (err)=>method === 'get' && !disableRetry && new ErrorResponse(err).status === 503;
        return attempt().catch(async (err)=>{
            if (!isReplayableGatewayFailure(err)) throw err;
            await delay(jitteredRetryBackoffMs(random()));
            return attempt();
        }).then((response)=>{
            if (!response.status) {
                throw new AxiosError('Network Error', AxiosError.ERR_NETWORK, response.config, response.request);
            }
            if (prevCookie !== getCookie()) {
                onCookieChange();
            }
            if (getState().session.useAuthorizationHeader) {
                const body = response.data;
                if (body != null && typeof body === 'object' && typeof body.authToken === 'string' && body.authToken.length > 0) {
                    onAuthTokenInResponse(body.authToken);
                }
            }
            return response;
        }).catch((err)=>{
            var _getState_claims;
            const e = new ErrorResponse(err);
            const is401 = e.status === 401;
            const is403ForReviewer = e.status === 403 && selectors.user.isReviewer(getState());
            const isImpersonating = !!selectors.impersonatingAs(getState());
            if (is401 && getState().session.useAuthorizationHeader) {
                // WHY: the bearer token is sent to every host, so a stale one 401s on any of them — purge it
                // everywhere or the next boot re-hydrates the dead token and loops (ANCR-3674). The biometric
                // disable must fire before onAuthTokenRevoked, which clears the restored marker it reads.
                onBiometricStaleToken();
                onAuthTokenRevoked();
            }
            if (is401) onLogout();
            const is403DuringBusinessSwitch = e.status === 403 && getState().isSwitchingBusiness;
            const is403FromStaleBusinessUrl = e.status === 403 && isUrlBusinessIdStale(fullUrl, (_getState_claims = getState().claims) === null || _getState_claims === void 0 ? void 0 : _getState_claims.businessId);
            const skipNoAccessNavigation = is403DuringBusinessSwitch || is403FromStaleBusinessUrl;
            const is403HandlingDisabled = typeof disable403Handling === 'function' ? disable403Handling(e) : !!disable403Handling;
            const is403HandledByNoAccess = !is403HandlingDisabled && e.status === 403 && !skipNoAccessNavigation;
            if (is403HandledByNoAccess) onNoAccess();
            const disableSentryReporting = isImpersonating || is401 || is403ForReviewer || is403DuringBusinessSwitch || is403FromStaleBusinessUrl || is403HandledByNoAccess || typeof disableSentry === 'function' && disableSentry(e);
            if (!disableSentryReporting && e.status) {
                const serverErrorCode = e.serverErrorCode();
                onReportToSentry(_object_spread({
                    message: "Request (".concat(method.toUpperCase(), " ").concat(urlForSentry || fullUrl, ") failed with status code ").concat(e.status).concat(serverErrorCode ? " (error code: ".concat(serverErrorCode, ")") : ''),
                    requestId,
                    method,
                    url: fullUrl,
                    responseBody: e.body ? JSON.stringify(e.body) : ''
                }, identityAtSend));
            }
            throw err;
        });
    };
    const sendStream = async (options)=>{
        const state = getState();
        const { fullUrl, headers, requestId, prevCookie } = buildRequestInputs({
            apiHost: options.apiHost,
            url: options.url,
            disableAuth: options.disableAuth,
            authToken: undefined,
            forceAuthorizationHeader: false
        }, state, getCookie);
        await fetchEventSource(fullUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(options.data),
            credentials: 'include',
            signal: options.signal,
            openWhenHidden: true,
            onopen: async (res)=>{
                var _res_headers_get;
                if (prevCookie !== getCookie()) onCookieChange();
                if (res.status === 401) {
                    onBiometricStaleToken();
                    onLogout();
                    throw new FatalStreamError('unauthorized', 401);
                }
                if (res.status === 403) {
                    onNoAccess();
                    throw new FatalStreamError('forbidden', 403);
                }
                if (!res.ok) {
                    // WHY the body is read before throwing: the server answers a refused tap with a JSON error
                    // code and no stream, so discarding it here is what leaves the pane with only a bare status.
                    const refusal = await res.json().catch(()=>null);
                    const errorCode = typeof refusal === 'object' && refusal !== null && 'error' in refusal ? String(refusal.error) : undefined;
                    throw new FatalStreamError("stream open failed: ".concat(res.status), res.status, errorCode);
                }
                const contentType = (_res_headers_get = res.headers.get('content-type')) !== null && _res_headers_get !== void 0 ? _res_headers_get : '';
                if (!contentType.includes('text/event-stream')) {
                    throw new FatalStreamError('expected text/event-stream');
                }
            },
            onmessage: (evt)=>{
                if (evt.event === 'done') return;
                const parsed = options.parseEvent(evt);
                if (parsed == null) return;
                options.onEvent(parsed);
            },
            onerror: (err)=>{
                const isAuthErr = err instanceof FatalStreamError && (err.status === 401 || err.status === 403);
                const isImpersonating = !!selectors.impersonatingAs(state);
                const appErr = err instanceof ErrorResponse ? err : new ErrorResponse({
                    response: {
                        status: err instanceof FatalStreamError ? err.status : undefined,
                        // WHY: the refusal code is what tells an expected decline from a real failure.
                        data: err instanceof FatalStreamError ? {
                            error: err.errorCode
                        } : undefined
                    }
                });
                const disableByPredicate = typeof options.disableSentry === 'function' && options.disableSentry(appErr);
                const shouldReport = !isAuthErr && !isImpersonating && !disableByPredicate;
                if (shouldReport) {
                    var _state_claims, _state_userInfo;
                    onReportToSentry({
                        message: "Stream (POST ".concat(options.urlForSentry || fullUrl, ") failed: ").concat(err),
                        requestId,
                        method: 'POST',
                        url: fullUrl,
                        responseBody: '',
                        claimsBusinessId: ((_state_claims = state.claims) === null || _state_claims === void 0 ? void 0 : _state_claims.businessId) || null,
                        userInfoBusinessId: ((_state_userInfo = state.userInfo) === null || _state_userInfo === void 0 ? void 0 : _state_userInfo.businessId()) || null,
                        isSwitchingBusiness: !!state.isSwitchingBusiness
                    });
                }
                throw err;
            }
        });
    };
    return {
        sendRequest,
        sendStream
    };
}
